import { ensureArray } from '@/utils/siteUtils';

// all local storage items must be stored under this namespace, or they will be deleted
const DEFAULT_NAMESPACE = 'bnef';
const SEPARATOR = '.';

/*
    A Utility for saving preferences on the client browser,
    in a way that prevents persistent storage bloat by cleaning up old/expired items.
    If this code is called on the server it will be a no-op.
 */
class BrowserStorage {
    protected readonly storage: Storage | undefined;
    private readonly namespace: string;

    constructor(storage: Storage | undefined, namespace: string = DEFAULT_NAMESPACE) {
        this.storage = storage;
        this.namespace = namespace;
    }

    protected save(key: string | string[], payload: any): boolean {
        if (!this.storage) {
            return false;
        }

        try {
            this.storage.setItem(this.getKey(key), JSON.stringify(payload));
            return true;
        } catch (err) {
            console.warn('Error writing to storage', key, err);
            return false;
        }
    }

    protected retrieve<T = any>(key: string | string[], rawKey = false): T | undefined {
        if (!this.storage) {
            return;
        }
        const storageKey = rawKey && typeof key === 'string' ? key : this.getKey(key);
        const item = this.storage.getItem(storageKey);
        if (!item) {
            return;
        }
        try {
            return JSON.parse(item);
        } catch {
            // all of our keys should be valid JSON. If we can't parse it, remove it.
            this.storage.removeItem(storageKey);
        }
    }

    private getKey(key: string | string[]) {
        const keyParts = ensureArray(key);
        return `${this.namespace}${SEPARATOR}${keyParts.join(SEPARATOR)}`;
    }

    public remove(key: string | string[]): void {
        if (!this.storage) {
            return;
        }
        this.storage.removeItem(this.getKey(key));
    }

    public clearNamespace(namespace: string | string[]): void {
        if (!this.storage) {
            return;
        }
        const key = this.getKey(namespace) + SEPARATOR;
        for (const storageKey of this.getKeys()) {
            if (storageKey.startsWith(key)) {
                this.storage.removeItem(storageKey);
            }
        }
    }

    public getKeys() {
        return this.storage ? Object.keys(this.storage) : [];
    }

    public clearAll(): void {
        if (!this.storage) {
            return;
        }
        this.storage.clear();
    }
}

export default BrowserStorage;
